import { useMemo } from 'react';
import orderBlockStyles from '~/styles/bookingFlow/order-block.module.css';
import formatDate from '~/helpers/formatDate';
import type { PackageSessionResponse } from '~/types/api';
import OrderBlockWrapper from './OrderBlockWrapper';
import OptionGroup from './OptionGroup';
import OrderBlockLine from './OrderBlockLine';

type Props = {
  orderBlock: PackageSessionResponse['order_blocks'][0];
  pax: number;
  optionGroups: Record<string, PackageSessionResponse['order_lines']>;
  onUpdateOptionQuantity: (
    optionId: number,
    orderRoomTypeId: number | null,
    quantities: number[],
  ) => void;
};

export default function OrderBlock({
  orderBlock,
  pax,
  optionGroups,
  onUpdateOptionQuantity,
}: Props) {
  const dateRange = useMemo(() => {
    const startDateFormatted = orderBlock.start_date
      ? formatDate(orderBlock.start_date, 'd-m-Y')
      : null;
    const endDateFormatted = orderBlock.end_date
      ? formatDate(orderBlock.end_date, 'd-m-Y')
      : null;

    if (!startDateFormatted && !endDateFormatted) {
      return null;
    }

    if (startDateFormatted === endDateFormatted) {
      return startDateFormatted;
    }

    return [startDateFormatted, endDateFormatted].join(' – ');
  }, [orderBlock.start_date, orderBlock.end_date]);

  const visibleOptions = useMemo(() => {
    return Object.keys(optionGroups).filter((name) => {
      return optionGroups[name]
        .some((orderLine) => orderLine.visible === undefined || orderLine.visible === true);
    });
  }, [optionGroups]);

  const hideBlockName = useMemo(() => {
    return orderBlock.name.toLowerCase() === 'empty';
  }, [orderBlock.name]);

  const hideDateRange = useMemo(() => {
    return orderBlock.name.toLowerCase() === 'empty';
  }, [orderBlock.name]);

  if (!visibleOptions.length && !orderBlock.name) {
    return null;
  }

  if (!visibleOptions.length && orderBlock.display_type === 'Website_below') {
    return null;
  }

  return (
    <OrderBlockWrapper key={orderBlock.name}>
      {!hideBlockName && orderBlock.name && (
        <h2 className={orderBlockStyles.orderBlockTitle}>
          {orderBlock.name}
        </h2>
      )}

      {!hideDateRange && dateRange && orderBlock.start_date && (
        <time
          dateTime={formatDate(orderBlock.start_date, 'Y-m-d')}
          className={orderBlockStyles.orderBlockTime}
        >
          {dateRange}
        </time>
      )}

      {visibleOptions.length > 0 && (
        <div className={orderBlockStyles.options}>
          {optionGroups && Object.keys(optionGroups).map((name) => (
            // eslint-disable-next-line react/no-array-index-key
            <OptionGroup key={name} name={name}>
              {optionGroups[name].map((orderLine, index) => {
                return (
                  <OrderBlockLine
                    // eslint-disable-next-line react/no-array-index-key
                    key={`${orderLine.id}-${index}`}
                    pax={pax}
                    orderLine={orderLine}
                    onUpdateOptionQuantity={onUpdateOptionQuantity}
                  />
                );
              })}
            </OptionGroup>
          ))}
        </div>
      )}
    </OrderBlockWrapper>
  );
}
